import { Box, Fade, styled } from '@mui/material';
import classNames from 'classnames';
import { useMemo } from 'react';

import { HubsViewMode, useHorizontalRowScroll } from '#shared';

import { useRegionedHubs, useRegionedSurgeonGroups } from '../../data-context';
import { subRegionMap } from '../../helpers';
import { HubStatusCardList, renderRegionCards } from '../../HubStatusCardList';

import { mainMapOverlayClasses } from './MapOverlay.styled';

import type { HubReportStatus } from '#entities';

interface HubsByRegionListProps {
  hubs: HubReportStatus[];
  handleOpenHubDrawer: (hubId: string) => void;
  handleOpenSurgeonDrawer: (hubId: string) => void;
  hubsViewMode?: HubsViewMode;
}

const FlatScrollRow = styled(Box)(({ theme: { spacing } }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  gap: spacing(4),
  flexWrap: 'nowrap',
  overflowX: 'auto',
  overflowY: 'hidden',
  WebkitOverflowScrolling: 'touch', // iOS
  overscrollBehaviorX: 'contain', // keep wheel from bubbling to page
  overscrollBehaviorY: 'none',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',

  '&::-webkit-scrollbar': {
    display: 'none',
  },
}));

export const HubsByRegionList = ({
  hubs,
  handleOpenHubDrawer,
  handleOpenSurgeonDrawer,
  hubsViewMode = HubsViewMode.Regions,
}: HubsByRegionListProps) => {
  const regionSurgeons = useRegionedSurgeonGroups();
  const { regions } = useRegionedHubs(hubs);
  const rowRef = useHorizontalRowScroll<HTMLDivElement>();

  const allCardsFlat = useMemo(
    () =>
      hubsViewMode === HubsViewMode.Flat
        ? regions.flatMap(({ key, hubs, region }) =>
            renderRegionCards(
              region,
              regionSurgeons[key],
              hubs,
              handleOpenHubDrawer,
              index => {
                const subRegions = subRegionMap[region];
                const regionName = subRegions[index] ?? subRegions[0];
                handleOpenSurgeonDrawer(regionName);
              },
              hubsViewMode,
            ),
          )
        : [],
    [
      regions,
      regionSurgeons,
      handleOpenHubDrawer,
      handleOpenSurgeonDrawer,
      hubsViewMode,
    ],
  );

  return (
    <>
      {/* Flat layout with fade-in */}
      <Fade
        in={hubsViewMode === HubsViewMode.Flat}
        timeout={{ exit: 0, appear: 500, enter: 500 }}
        mountOnEnter
        unmountOnExit
      >
        <div
          className={classNames(
            mainMapOverlayClasses.hubs,
            mainMapOverlayClasses.hubsHorizontal,
          )}
        >
          <FlatScrollRow ref={rowRef}>{allCardsFlat}</FlatScrollRow>
        </div>
      </Fade>

      {/* Region layout with fade-in */}
      {regions.map(({ key, hubs, alignment, className, region }) => (
        <Fade
          key={key}
          in={hubsViewMode === HubsViewMode.Regions}
          timeout={{ exit: 0, appear: 500, enter: 500 }}
          mountOnEnter
          unmountOnExit
        >
          <div>
            <HubStatusCardList
              hubsViewMode={hubsViewMode}
              items={hubs}
              alignment={alignment}
              className={classNames(mainMapOverlayClasses.hubs, className)}
              region={region}
              surgeonGroups={regionSurgeons[key]}
              onClickHub={handleOpenHubDrawer}
              onClickSurgeon={index => {
                const subRegions = subRegionMap[region];
                const regionName = subRegions[index] ?? subRegions[0];
                handleOpenSurgeonDrawer(regionName);
              }}
            />
          </div>
        </Fade>
      ))}
    </>
  );
};
